import {
    Autocomplete,
    Chip,
    InputAdornment,
    Link,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import React, { useCallback, useState } from "react";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import CopyrightOutlinedIcon from "@mui/icons-material/CopyrightOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { rightService } from "../../../../../Core/Api/services";
import { useOutletContext, useParams } from "react-router";
import { Link as RouterLink } from "react-router";
import { routePaths } from "../../../../../Router/router";
import { RightType } from "../../../../../Core/Api/Types/right-type";
import { GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid-premium";
import { DbAccessDealInfos } from "../../../../../Core/Api/Types/db-access-deal-infos";
import TableCellDate2Lines from "../../../../../Components/Table/TableCellDate2Lines";
import moment from "moment";
import TableCellBool from "../../../../../Components/Table/TableCellBool";
import TableCellChip from "../../../../../Components/Table/TableCellChip/table-cell-chip";
import DataTable, { CellSize } from "../../../../../Components/Table/table";
import { useToggle } from "../../../../../Utils/Hooks/useToggle";
import { useSelector } from "react-redux";
import { GetObject } from "../../../../../Redux/Reducers/redux-slice-asset";
import TableSkeleton from "../../../../../Components/Table/TableSkeleton";
import ContractDetailsPanel from "../../../../../Components/Panels/ContractDetailsPanel/contract-panel";
import { DistinctByProp } from "../../../../../Utils/helpers";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import useBasketActions from "../../../../../Utils/Hooks/useBasketActions";
import { ContractAndRightType } from "../../../../../Core/Api/Right/right-api-service";
import TableCellTypography from "../../../../../Components/Table/TableCellTypography/table-cell-typography";
import ListPageHeader, {
    ListPageHeaderGroupActions,
    ListPageHeaderRight,
} from "../../../../../Components/Misc/ListPageHeader";
import { useTranslation } from "react-i18next";
import TableUuids from "../../../../../Components/Table/table-uuids";

function AdminTitleContracts() {
    const { t } = useTranslation();
    const [rows, setDataGrid] = React.useState<ContractAndRightType[]>([]);
    const [customers, setDataCustomers] = React.useState<string[]>([]);
    const [isLoading, setLoading] = React.useState(true);
    const [filterCustomer, setFilterCustomer] = React.useState("");
    const [filterType, setFilterType] = React.useState("");
    const [filterText, setFilterText] = React.useState("");
    const [filterMediaNames, setFilterMediaNames] = React.useState<string[]>([]);
    const [mediaNamesOptions, setMediaNamesOptions] = React.useState<string[]>([]);
    const [filterLangs, setFilterLangs] = React.useState<any[]>([]);
    const [filterTerritories, setFilterTerritories] = React.useState<any[]>([]);

    const params = useParams();
    const titleId: number = parseInt(params.id!) || 0;

    const titleInfos = useSelector(GetObject);
    const { addContractsToBasket } = useBasketActions();

    const [contractDetailsPanelOpen, setContractDetailsPanelOpen] = useState<boolean>(false);

    const [selectedContract, setSelectedContract] = React.useState<ContractAndRightType>();
    // Outlet context used to pass props to parent DashboardLayout through react router dom
    const { setSelectedContracts, selectedContracts } = useOutletContext<{
        setSelectedContracts: React.Dispatch<React.SetStateAction<ContractAndRightType[]>>;
        selectedContracts: ContractAndRightType[];
    }>();

    const showPanelContractDetails = (e: any) => {
        hidePanelContractDetails();
        setSelectedContract(e.row);
        setContractDetailsPanelOpen(true);
    };

    const hidePanelContractDetails = () => {
        setContractDetailsPanelOpen(false);
    };

    const columns: GridColDef[] = [
        {
            field: "customerName",
            headerName: t("common:customer"),
            minWidth: CellSize.medium,
            flex: CellSize.medium,
            renderCell: (data: any) => (
                <>
                    {data?.row?.customerId && data?.row?.customerId > 0 ? (
                        <Link
                            color="primary.text"
                            underline="hover"
                            component={RouterLink}
                            to={`${routePaths.customers.path}/${data?.row?.customerId}`}
                            sx={{ py: 1 }}
                        >
                            {data.value}
                        </Link>
                    ) : (
                        <Typography sx={{ py: 2 }}>{t("page:body.noLinkedCustomer")}</Typography>
                    )}
                </>
            ),
        },
        {
            field: "type",
            headerName: t("common:type"),
            minWidth: CellSize.small,
            flex: CellSize.small,
            renderCell: (data: any) => <Chip size="small" label={data.value} />,
        },
        {
            field: "startDate",
            headerName: t("table:headerName.startAt"),
            minWidth: CellSize.small,
            flex: CellSize.small,
            renderCell: (data: any) => <TableCellDate2Lines date={data.value} />,
        },
        {
            field: "endDate",
            headerName: t("table:headerName.expireAt"),
            minWidth: CellSize.small,
            flex: CellSize.small,
            renderCell: (data: any) => <TableCellDate2Lines date={data.value} />,
        },
        {
            field: "active",
            headerName: t("common:active"),
            minWidth: CellSize.xsmall,
            flex: CellSize.xsmall,
            renderCell: (data: any) => <TableCellBool value={data.value} />,
        },
        {
            field: "broadcasterName",
            headerName: t("common:broadcaster"),
            minWidth: CellSize.small,
            flex: CellSize.small,
            renderCell: (data: any) => <TableCellTypography data={data.value === null ? "-" : data.value} />,
        },
        {
            field: "externalId",
            headerName: t("table:headerName.externalId"),
            minWidth: CellSize.small,
            flex: CellSize.small,
            renderCell: (data: any) => (
                <TableCellTypography data={data.value === null ? "-" : data.value} variant="body1" color="text.secondary" />
            ),
        },
        {
            field: "customerCodeId",
            headerName: t("table:headerName.customerCodeId"),
            minWidth: CellSize.medium,
            flex: CellSize.medium,
            renderCell: (data: any) => (
                <TableCellTypography data={data.value === null ? "-" : data.value} variant="body1" color="text.secondary" />
            ),
        },
        {
            field: "workRecordId",
            headerName: t("table:headerName.workId"),
            minWidth: CellSize.xsmall,
            flex: CellSize.xsmall,
            renderCell: (data: any) => (
                <TableCellTypography data={data.value === null ? "-" : data.value} variant="body1" color="text.secondary" />
            ),
        },
        {
            field: "languages",
            headerName: t("common:languages"),
            minWidth: CellSize.xsmall,
            flex: CellSize.xsmall,
            renderCell: (data: any) => <TableCellChip data={{ value: [data.value.length] }} color="primary" />,
        },
        {
            field: "territories",
            headerName: t("common:territories"),
            minWidth: CellSize.xsmall,
            flex: CellSize.xsmall,
            renderCell: (data: any) => <TableCellChip data={{ value: [data.value.length] }} color="primary" />,
        },
        {
            field: "actions",
            headerName: "",
            type: "actions",
            minWidth: CellSize.actionsSmall,
            flex: CellSize.actionsSmall,
            align: "right",
            getActions: (params: GridRowParams) => [
                <GridActionsCellItem
                    icon={<ShoppingBagOutlinedIcon sx={{ width: 20, height: 20 }} />}
                    label={t("common:addToBasket")}
                    onClick={() => handleAddToBasket([params.row])}
                />,
            ],
        },
    ];

    const handleAddToBasket = useCallback((rows: ContractAndRightType[]) => {
        addContractsToBasket(rows);
    }, []);

    const mapDataToGrid = (deals: DbAccessDealInfos[], rightId: number): ContractAndRightType[] => {
        let output: ContractAndRightType[] = [];
        let mediaNames: string = "";

        let i: number = 0;
        let now: any = moment();
        let customers: string[] = [];

        deals?.forEach((deal) => {
            if (deal.customerName !== null && customers.indexOf(deal.customerName) === -1) customers.push(deal.customerName);

            deal.rights?.forEach((right) => {
                if (right.mediaDetails)
                    mediaNames += right.mediaDetails.map((item) => (item.mediaName ? item.mediaName : "")).join(",");

                output.push({
                    id: i,
                    titleId: titleId,
                    dealsNumber: 1,
                    contractId: rightId,
                    type: deal.rightType ?? "Unknown",
                    startDate: right.startDate,
                    endDate: right.endDate,
                    active: moment(right.endDate) > now,
                    okDelivery: deal.okDelivery,
                    externalId: deal.externalId ?? "",
                    workRecordId: deal.workRecordId,
                    customerName: deal.customerName ?? "",
                    customerId: deal.customerId ?? -1,
                    customerCodeId: deal.customerCodeId,
                    broadcasterName: deal.broadcasterName ?? "",
                    mediaNamesString: right.mediaDetails ? right.mediaDetails.map((item) => item.mediaName).join(", ") : "",
                    languages: right.languageCodes
                        ? Object.keys(right.languageCodes!).map((value) => ({
                            key: right.languageCodes![value]["key"],
                            value: right.languageCodes![value]["value"],
                        }))
                        : [],
                    territories: right.territoryCodes
                        ? Object.keys(right.territoryCodes!).map((value) => ({
                            key: right.territoryCodes![value]["key"],
                            value: right.territoryCodes![value]["value"],
                        }))
                        : [],
                });

                ++i;
                mediaNames += ",";
            });
        });

        setMediaNamesOptions([
            ...new Set(
                mediaNames
                    .split(",")
                    .map((str) => str.trim())
                    .filter((x) => x !== "")
            ),
        ]);
        setDataCustomers(customers);
        return output;
    };

    const handleSetSelectedContracts = useCallback(
        (ids: number[]) => {
            setSelectedContracts(rows.filter((x) => ids.includes(x.id)));
        },
        [rows]
    );

    React.useEffect(() => {
        if (titleId !== 0)
            rightService.GetRightContractByTitleIdBasic(titleId, RightType.All).then(function (response) {
                if (!response || !response.right) {
                    setLoading(false);
                    return;
                }

                let mappedData = mapDataToGrid(response.right.deals!, response.right.id);
                setDataGrid(mappedData);
                setLoading(false);
            });
    }, [titleId]);

    const additionalFilters = (
        <>
            <Select
                displayEmpty
                startAdornment={<HandshakeOutlinedIcon />}
                IconComponent={ExpandMoreRoundedIcon}
                value={filterCustomer}
                inputProps={{ "aria-label": "Without label" }}
                onChange={(e) => setFilterCustomer(e.target.value)}
                MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
            >
                <MenuItem value="">{t("common:customer")}</MenuItem>
                {customers.map((x) => (
                    <MenuItem value={x} key={x}>
                        {x}
                    </MenuItem>
                ))}
            </Select>

            <Select
                displayEmpty
                startAdornment={<CopyrightOutlinedIcon />}
                IconComponent={ExpandMoreRoundedIcon}
                value={filterType}
                inputProps={{ "aria-label": "Without label" }}
                onChange={(e) => setFilterType(e.target.value)}
                MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
            >
                <MenuItem value="">{t("menu:item.rightType")}</MenuItem>
                <MenuItem value="Disposal">{t("menu:item.disposal")}</MenuItem>
                <MenuItem value="Acquisition">{t("menu:item.acquisition")}</MenuItem>
            </Select>

            <Autocomplete
                value={filterLangs || []}
                multiple
                onChange={(e, value) => setFilterLangs(value)}
                options={DistinctByProp(
                    rows.flatMap((r) => r.languages),
                    "key"
                )}
                getOptionLabel={(option) => option.value + " (" + option.key + ")"}
                renderInput={(params) => (
                    <TextField sx={{ mt: "-24px" }} {...params} placeholder={t("common:languages")} />
                )}
                sx={{ minWidth: 150 }}
            />

            <Autocomplete
                value={filterTerritories || []}
                multiple
                onChange={(e, value) => setFilterTerritories(value)}
                options={DistinctByProp(
                    rows.flatMap((r) => r.territories),
                    "key"
                )}
                getOptionLabel={(option) => option.value}
                renderInput={(params) => (
                    <TextField sx={{ mt: "-24px" }} {...params} placeholder={t("common:territories")} />
                )}
                sx={{ minWidth: 150 }}
            />

            <Autocomplete
                value={filterMediaNames || []}
                multiple
                onChange={(e, value) => setFilterMediaNames(value)}
                options={mediaNamesOptions}
                renderInput={(params) => (
                    <TextField sx={{ mt: "-24px" }} {...params} placeholder={t("common:mediaNames")} />
                )}
                sx={{ minWidth: 150 }}
            /></>
    )

    return (
        <Stack minHeight={"calc(100vh - 129px)"}>
            <DataTable
                loading={isLoading}
                tableKey={TableUuids.TITLE_SINGLE_CONTRACTS}
                rows={rows.filter(
                    (x) =>
                        (filterCustomer === "" || x.customerName === filterCustomer) &&
                        (filterType === "" || x.type === filterType) &&
                        (filterText === "" || x.customerName?.includesAllWords(filterText)) &&
                        (filterLangs.length === 0 ||
                            x.languages.filter((y) => filterLangs.map((l) => l.key).includes(y.key)).length > 0) &&
                        (filterTerritories.length === 0 ||
                            x.territories.filter((y) => filterTerritories.map((l) => l.key).includes(y.key)).length > 0) &&
                        (filterMediaNames.length === 0 ||
                            (x.mediaNamesString !== undefined &&
                                x.mediaNamesString.length > 0 &&
                                filterMediaNames?.every((item) =>
                                    [
                                        ...new Set(
                                            x.mediaNamesString
                                                ?.split(",")
                                                .map((str) => str.trim())
                                                .filter((x) => x !== "")
                                        ),
                                    ].includes(item)
                                )))
                )}
                additionalFilters={additionalFilters}
                columns={columns}
                disableRowSelectionOnClick
                pagination={true}
                getRowHeight={() => "auto"}
                initialState={{
                    sorting: { sortModel: [{ field: "start", sort: "desc" }] },
                    pinnedColumns: { left: ["__check__", "customerName"], right: ["actions"] },
                }}
                onRowClick={showPanelContractDetails}
                setSelectedRows={(e) => {
                    handleSetSelectedContracts(e as number[]);
                }}
                selectedRows={selectedContracts.map((x) => x.id)}
                checkboxSelection
            />
            <ContractDetailsPanel
                open={contractDetailsPanelOpen}
                onClose={hidePanelContractDetails}
                contract={selectedContract ? { ...selectedContract!, titleInfos: titleInfos, titleId: titleId } : undefined}
            />
        </Stack>
    );
}

export default AdminTitleContracts;
